import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Row,
  Col,
  Input,
  Label,
  Form,
  FormGroup
} from 'reactstrap';
import classnames from 'classnames';
import TableContainerReactTable from '../../../../Components/Common/TableContainerReactTable';
import FormInput from '../common/FormInput';
import SelectInput from '../common/SelectInput';
import { ParentProductData, ProductDetailsData } from 'features/products/types/types';
import { useLazyGetParentProductQuery } from 'features/products/redux/productApiSlice';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import LoadingIndicator from 'Components/Loading/LoadingIndicator';
import { ProductBundleItem } from 'features/products/types/ProductBundleTypes';


interface ParentProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect?: (product: ParentProductData) => void;
  selectionModal?: 'pick' | 'qty';
  onSelectQty?: (product: ParentProductData, quantity: number) => void;
  excludeBundleItems?: ProductBundleItem[];
}


const searchFieldOptions: Array<{ label: string; value: keyof ParentProductData }> = [
  { label: 'Product Code', value: 'productCode' },
  { label: 'Product Name', value: 'productName' },
];

// New Product Form Data interface
interface NewProductFormData {
  name: string;
  productCode: string;
  vendor: string;
  vendorProductCode: string;
  type: string;
  subtype: string;
  marginType: string;
  category: string;
  subcategory: string;
  active: boolean;
  orderable: boolean;
  taxable: boolean;
  description: string;
  brand: string;
  brandProductCode: string;
  barcode: string;
  orderQty: string;
  purchaseExc: string;
  purchaseTax: string;
  purchaseInc: string;
  markup: string;
  sellExc: string;
  sellTax: string;
  sellInc: string;
  nzStatus: string;
  addToAll: boolean;
  pickQuantity: string;
  priceList: boolean;
  location: boolean;
  dangerousGoods: boolean;
}

const defaultNewProductForm: NewProductFormData = {
  name: '',
  productCode: '',
  vendor: '',
  vendorProductCode: '',
  type: '',
  subtype: '',
  marginType: '',
  category: '',
  subcategory: '',
  active: true,
  orderable: true,
  taxable: true,
  description: '',
  brand: '',
  brandProductCode: '',
  barcode: '',
  orderQty: '',
  purchaseExc: '',
  purchaseTax: 'GST - 10%',
  purchaseInc: '',
  markup: '',
  sellExc: '',
  sellTax: 'GST - 10%',
  sellInc: '',
  nzStatus: 'Always Available',
  addToAll: false,
  pickQuantity: '1',
  priceList: true,
  location: true,
  dangerousGoods: false,
};

const purchaseTaxOptions = ['GST - 10%', 'GST - 0%', 'None'];
const sellTaxOptions = ['GST - 10%', 'GST - 0%', 'None'];
const nzStatusOptions = ['Always Available', 'Not Available', 'Limited'];
const typeOptions = ['Product', 'Service', 'Bundle'];
const subtypeOptions = ['New', 'Used', 'Refurbished'];
const categoryOptions = ['Hair Appliances', 'Haircare', 'Makeup', 'Fragrance'];
const subcategoryOptions = ['Subcat 1', 'Subcat 2', 'Subcat 3'];
const marginTypeOptions = ['Gross', 'Net', 'Markup'];

const ParentProductModal = ({ isOpen, onClose, onSelect, selectionModal = 'pick', onSelectQty, excludeBundleItems = [] }: ParentProductModalProps) => {
  const [activeTab, setActiveTab] = useState('1');
  const [searchField, setSearchField] = useState<keyof ParentProductData>('productCode');
  const [searchValue, setSearchValue] = useState('');
  const [products, setProducts] = useState<ParentProductData[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<ParentProductData | null>(null);
  const [onlyOrderable, setOnlyOrderable] = useState(false);
  const [autoAdd, setAutoAdd] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [newProductForm, setNewProductForm] = useState<NewProductFormData>(defaultNewProductForm);

  // Search form state
  const [searchProductName, setSearchProductName] = useState('');
  const [searchProductCode, setSearchProductCode] = useState('');
  const [searchCategory, setSearchCategory] = useState('');
  const [searchVendor, setSearchVendor] = useState('');
  const [searchBarcode, setSearchBarcode] = useState('');


  // per-row qty editing (keyed by productCode)
  const [editedQuantities, setEditedQuantities] = useState<Record<string, string>>({});
  const editedQuantitiesRef = useRef<Record<string, string>>({});
  useEffect(() => {
    editedQuantitiesRef.current = editedQuantities;
  }, [editedQuantities]);

  // Reset search fields when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSearchProductName('');
      setSearchProductCode('');
      setSearchCategory('');
      setSearchVendor('');
      setSearchBarcode('');
      setOnlyOrderable(false);
      setSelectedProduct(null);
    }
  }, [isOpen]);

  // Automatically fetch fresh data when modal opens
  useEffect(() => {
    if (isOpen) {
      // Trigger search with default parameters to fetch fresh data
      // This will always make a new API call when modal opens
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  //redux store
  const { selectedProduct: selectedProductStore } = useSelector((state: RootState) => state.products);

  //redux api
  const [getParentProductApi, { isLoading: isLoadingParentProduct, data: parentProductData, isSuccess: isSuccessParentProduct }] = useLazyGetParentProductQuery();

  // Filter products to exclude those already in bundle
  const filteredProducts = useMemo(() => {
    if (!parentProductData?.data || excludeBundleItems.length === 0) {
      return parentProductData?.data || [];
    }

    // Create sets of product names and IDs from bundle items for quick lookup
    const bundleProductNames = new Set(
      excludeBundleItems.map(item => item.productName?.toLowerCase().trim()).filter(Boolean)
    );
    const bundleProductIds = new Set(
      excludeBundleItems.map(item => item.childProductId).filter(id => id != null)
    );

    return parentProductData.data.filter((product: ParentProductData) => {
      // Filter out products that match by name (case-insensitive)
      if (product.productName && bundleProductNames.has(product.productName.toLowerCase().trim())) {
        return false;
      }

      // Filter out products that match by rowId (childProductId in bundle)
      // This is the most reliable identifier for matching products
      if (product.rowId && bundleProductIds.has(product.rowId)) {
        return false;
      }

      return true;
    });
  }, [parentProductData?.data, excludeBundleItems]);

  const columns = useMemo(() => [
    {
      header: selectionModal === 'pick' ? 'Pick' : 'Qty',
      accessorKey: 'pick',
      enableColumnFilter: false,
      cell: ({ row }: { row: { original: ParentProductData } }) => (
        selectionModal === 'pick' ? (
          <Input
            type="checkbox"
            checked={selectedProduct?.productCode === row.original.productCode}
            onChange={(e) => {
              e.stopPropagation(); // Prevent row selection when clicking checkbox
              if (e.target.checked) {
                setSelectedProduct(row.original as any);
                setShowDetails(false);
              } else {
                setSelectedProduct(null);
              }
            }}
          />
        ) : (
          (() => {
            const key = row.original.productCode;
            const displayValue = editedQuantitiesRef.current[key] ?? '';
            return (
              <div
                className="d-flex justify-content-start"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="text"
                  className="form-control form-control-sm text-center"
                  style={{ maxWidth: 90 }}
                  aria-label={`Quantity for ${row.original.productName || row.original.productCode}`}
                  value={displayValue}
                  onFocus={(e) => {
                    e.currentTarget.select();
                    setSelectedProduct(row.original as any);
                    setShowDetails(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    editedQuantitiesRef.current = { ...editedQuantitiesRef.current, [key]: newValue };
                    setEditedQuantities((prev) => ({ ...prev, [key]: newValue }));
                  }}
                />
              </div>
            );
          })()
        ))
    },
    {
      header: 'Product',
      accessorKey: 'productName',
      enableColumnFilter: false,
      cell: ({ row }: { row: { original: ParentProductData } }) => {
        const productName = row.original.productName || '';
        const displayText = productName.length > 30
          ? `${productName.substring(0, 30)}...`
          : productName;

        return (
          <span
            style={{ cursor: 'pointer' }}
            onClick={() => handleRowClick(row.original)}
            className={selectedProduct?.productCode === row.original.productCode ? 'fw-bold text-primary' : ''}
            title={productName} // Show full text on hover
          >
            {displayText}
          </span>
        );
      }
    },
    {
      header: 'Product Code',
      accessorKey: 'productCode',
      enableColumnFilter: false,
      cell: ({ row }: { row: { original: ParentProductData } }) => (
        <span
          style={{ cursor: 'pointer' }}
          onClick={() => handleRowClick(row.original)}
          className={selectedProduct?.productCode === row.original.productCode ? 'fw-bold text-primary' : ''}
        >
          {row.original.productCode}
        </span>
      )
    },
    {
      header: 'Type',
      accessorKey: 'type',
      enableColumnFilter: false,
      cell: ({ row }: { row: { original: ParentProductData } }) => {
        const type = row.original.type || '';
        const abbreviatedType = type
          .split(' ')
          .map(word => word.charAt(0).toUpperCase())
          .join('');

        return (
          <span
            style={{ cursor: 'pointer' }}
            onClick={() => handleRowClick(row.original)}
            className={selectedProduct?.productCode === row.original.productCode ? 'fw-bold text-primary' : ''}
          >
            {abbreviatedType}
          </span>
        );
      }
    },
    {
      header: 'Alloc',
      accessorKey: 'allocated',
      enableColumnFilter: false,
      cell: ({ row }: { row: { original: ParentProductData } }) => (
        <span
          style={{ cursor: 'pointer' }}
          onClick={() => handleRowClick(row.original)}
          className={selectedProduct?.productCode === row.original.productCode ? 'fw-bold text-primary' : ''}
        >
          {row.original.allocated}
        </span>
      )
    },
    {
      header: 'Avail',
      accessorKey: 'available',
      enableColumnFilter: false,
      cell: ({ row }: { row: { original: ParentProductData } }) => (
        <span
          style={{ cursor: 'pointer' }}
          onClick={() => handleRowClick(row.original)}
          className={selectedProduct?.productCode === row.original.productCode ? 'fw-bold text-primary' : ''}
        >
          {row.original.available}
        </span>
      )
    },
    {
      header: 'In WH',
      accessorKey: 'inWh',
      enableColumnFilter: false,
      cell: ({ row }: { row: { original: ParentProductData } }) => (
        <span
          style={{ cursor: 'pointer' }}
          onClick={() => handleRowClick(row.original)}
          className={selectedProduct?.productCode === row.original.productCode ? 'fw-bold text-primary' : ''}
        >
          {row.original.inWH}
        </span>
      )
    },
    {
      header: 'On Order',
      accessorKey: 'pickListDynamicVal',
      enableColumnFilter: false,
      cell: ({ row }: { row: { original: ParentProductData } }) => (
        <span
          style={{ cursor: 'pointer' }}
          onClick={() => handleRowClick(row.original)}
          className={selectedProduct?.productCode === row.original.productCode ? 'fw-bold text-primary' : ''}
        >
          {row.original.pickListDynamicVal}
        </span>
      )
    },
  ], [selectionModal]);

  // derive selected rows map for highlight in React Table
  const selectedRowKey = useMemo(() => {
    // prefer rowId if available to match getRowId logic
    // @ts-ignore
    const byRowId = selectedProduct?.rowId?.toString?.();
    return byRowId ?? selectedProduct?.productCode;
  }, [selectedProduct]);
  const selectedRows = useMemo(() => {
    return selectedRowKey ? { [selectedRowKey]: true } : {};
  }, [selectedRowKey]);

  const handleSearch = async () => {
    try {
      // Always fetch fresh data - RTK Query will make a new request
      await getParentProductApi({
        locId: 0,
        locIdTo: 0,
        priceListId: 0,
        productName: searchProductName.trim() || '%',
        productCode: searchProductCode.trim() || '%',
        category: searchCategory && searchCategory !== 'All' ? searchCategory : '%',
        vendor: searchVendor.trim() || '%',
        barcode: searchBarcode.trim() || '%',
        orderable: onlyOrderable,
      }).unwrap();

      if (isSuccessParentProduct) {
        // console.log('parentProductData', parentProductData);
      }
    } catch (error) {
      console.error('Error fetching parent product:', error);
    }
  };

  const handleClear = () => {
    setSearchValue('');
    setSearchProductName('');
    setSearchProductCode('');
    setSearchCategory('');
    setSearchVendor('');
    setSearchBarcode('');
    setOnlyOrderable(false);
    setProducts(products);
    setSelectedProduct(null);
  };

  const handleClears = () => {
    setSelectedProduct(null);
  };

  const handleCancel = () => {
    setSelectedProduct(null);
    onClose();
  };

  const handleShowDetails = () => {
    if (selectedProduct) {
      setShowDetails(!showDetails);
    }
  };

  const handleRowClick = (product: ParentProductData) => {
    setSelectedProduct(product as any); // Convert ParentProductData to ProductDetailsData
    setShowDetails(false); // Hide details when selecting a new row
  };

  const handleAdd = () => {
    if (selectedProduct) {
      onSelect?.(selectedProduct);
      onClose();
    }
  };

  const handlePickQty = () => {
    if (selectedProduct) {
      onSelectQty?.(selectedProduct, Number(editedQuantitiesRef.current[selectedProduct.productCode]));
      setEditedQuantities({});
      editedQuantitiesRef.current = {};
      onClose();
    }
  }

  const handleNewProductChange = (field: keyof NewProductFormData, value: any) => {
    setNewProductForm(prev => ({ ...prev, [field]: value }));
  };

  const handleNewProductCheckbox = (field: keyof NewProductFormData) => {
    setNewProductForm(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleNewProductClear = () => setNewProductForm(defaultNewProductForm);

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="xl">
      <ModalHeader toggle={onClose}>Pick Parent Product</ModalHeader>
      <ModalBody>
        <Nav pills className="nav nav-tabs-custom arrow-navtabs nav-success mb-3">
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '1' })}
              tabIndex={0}
              aria-label="Search Tab"
              onClick={() => setActiveTab('1')}
              style={{ cursor: 'pointer' }}
            >
              Search
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '2' })}
              tabIndex={0}
              aria-label="Advanced Search Tab"
              onClick={() => setActiveTab('2')}
              style={{ cursor: 'pointer' }}
            >
              Adv Search
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '3' })}
              tabIndex={0}
              aria-label="View Product Tab"
              onClick={() => setActiveTab('3')}
              style={{ cursor: 'pointer' }}
            >
              View Product
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '4' })}
              tabIndex={0}
              aria-label="New Product Tab"
              onClick={() => setActiveTab('4')}
              style={{ cursor: 'pointer' }}
            >
              New Product
            </NavLink>
          </NavItem>
        </Nav>
        <TabContent activeTab={activeTab}>
          <TabPane tabId="1">
            <Row className="justify-content-start align-items-center gap-2 ms-5">
              <Col md={2}>
                <FormInput
                  id="productName"
                  label="Product"
                  value={searchProductName}
                  onChange={(val) => setSearchProductName(val)}
                  className="form-control-sm"
                />
              </Col>

              <Col md={2}>
                <FormInput
                  id="vendor"
                  label="Vendor"
                  value={searchVendor}
                  onChange={(val) => setSearchVendor(val)}
                  className="form-control-sm"
                />
              </Col>
              <Col md={2}>
                <div className="form-check">
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    id="onlyOrderable"
                    checked={onlyOrderable}
                    onChange={(e) => setOnlyOrderable(e.target.checked)}
                  />
                  <Label className="form-check-label" htmlFor="onlyOrderable">
                    Only Orderable
                  </Label>
                </div></Col>
              <Col md={3} className="d-flex gap-2">
                <button className="btn btn-soft-primary btn-sm" style={{ minWidth: 80 }} onClick={handleSearch}>Search</button>
                <button className="btn btn-soft-warning btn-sm" style={{ minWidth: 80 }} onClick={handleClear}>Clear</button>
              </Col>
              <Col md={2}>
              </Col>
            </Row>
            <Row className="mb-2 justify-content-start align-items-center gap-2 ms-5">
              <Col md={2}>
                <FormInput
                  id="productCode"
                  label="Product Code"
                  value={searchProductCode}
                  onChange={(val) => setSearchProductCode(val)}
                  className="form-control-sm"
                />
              </Col>
              <Col md={2}>
                <SelectInput
                  formData={{ selectedCategory: searchCategory }}
                  setFormData={obj => setSearchCategory(obj.selectedCategory)}
                  label="Category"
                  id="selectedCategory"
                  options={['All', 'Outdoor', 'Garden', 'Plumbing', 'Furniture', 'Sets', 'Tools', 'Lighting', 'Motors', 'Electronics', 'Transport', 'Paint', 'Sports']}
                />
              </Col>
              <Col md={2}>
                <FormInput
                  id="barcode"
                  label="Barcode"
                  value={searchBarcode}
                  onChange={(val) => setSearchBarcode(val)}
                  className="form-control-sm"
                />
              </Col>
              <Col md={2}>
                <div className="form-check">
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    id="autoAdd"
                    checked={autoAdd}
                    onChange={(e) => setAutoAdd(e.target.checked)}
                  />
                  <Label className="form-check-label" htmlFor="autoAdd">
                    Auto Add
                  </Label>
                </div>
              </Col>
            </Row>
            {isLoadingParentProduct ? <LoadingIndicator
              isLoading={isLoadingParentProduct}
              isProcessing={false}
              error={null}
              title="Loading parent products from server..."
              height="200px"
            /> :
              <TableContainerReactTable
                columns={columns}
                data={filteredProducts}
                isGlobalFilter={false}
                customPageSize={10}
                tableClass="table-centered align-middle table-nowrap mb-0"
                theadClass="table-light"
                divClass="table-responsive"
                enableRowSelection={true}
                enableMultiRowSelection={false}
                rowSelection={selectedRows}
                getRowId={(row: any) => row?.rowId?.toString?.() || row?.productCode}
                onRowClick={(row: any) => handleRowClick(row.original)}
                verticalLines={true}
                enableVirtualScroll={true}
                tableBodyHeight="45vh"
                allowModeToggle={true}
              />}

            {showDetails && selectedProduct && (
              <Row className="justify-content-start align-items-center gap-2 mt-3">
                <fieldset className="border rounded p-2">
                  <legend className="float-none w-auto px-2 mb-0 text-body" style={{ fontSize: '0.9rem' }}>Product Profile</legend>
                  <Row>
                    <Col sm={4}>
                      <FormInput
                        id="name"
                        label="Name"
                        value={selectedProduct?.productName || ''}
                        onChange={() => { }}
                        required
                      />
                    </Col>
                    <Col sm={2}>
                      <FormInput
                        id="productId"
                        label="Product Id"
                        value={selectedProduct?.productCode || ''}
                        onChange={() => { }}
                      />
                    </Col>
                    <Col sm={2}>
                      <FormInput
                        id="type"
                        label="Type"
                        value="Product"
                        onChange={() => { }}
                      />
                    </Col>
                    <Col sm={2}>
                      <FormInput
                        id="productCode"
                        label="Product Code"
                        value={selectedProduct?.productCode || ''}
                        onChange={() => { }}
                      />
                    </Col>
                    <Col sm={2}>
                      <FormInput
                        id="subtype"
                        label="Subtype"
                        value="New"
                        onChange={() => { }}
                      />
                    </Col>
                    <Col sm={2}>
                      <FormInput
                        id="vendor"
                        label="Vendor"
                        value="Vendor A"
                        onChange={() => { }}
                      />
                    </Col>
                    <Col sm={2}>
                      <FormInput
                        id="category"
                        label="Category"
                        value="General"
                        onChange={() => { }}
                      />
                    </Col>
                    <Col sm={2}>
                      <FormInput
                        id="vProductCode"
                        label="V. Product Code"
                        value={selectedProduct?.barcode || ''}
                        onChange={() => { }}
                      />
                    </Col>
                    <Col sm={2}>
                      <FormInput
                        id="subcategory"
                        label="Subcategory"
                        value="Standard"
                        onChange={() => { }}
                      />
                    </Col>
                    <Col sm={4}>
                      <FormInput
                        id="extendedDetails"
                        label="Extended Details"
                        value="Next Delivery Date: Feb 5 2018 8:33AM"
                        onChange={() => { }}
                      />
                    </Col>
                  </Row>
                </fieldset>
              </Row>
            )}
          </TabPane>
          <TabPane tabId="2">
            <Row className="justify-content-center align-items-center">
              <Col md={12} className="text-center">
                <p className="text-muted">Advanced Search functionality coming soon...</p>
              </Col>
            </Row>
          </TabPane>
          <TabPane tabId="3">
            <Row>
              {/* Left Column */}
              <Col md={6}>
                <Row>
                  <Col md={12}>
                    <FormInput
                      id="viewProductName"
                      label="Name *"
                      value={selectedProduct?.productName || ''}
                      onChange={() => { }}
                      required
                    />
                  </Col>
                  <Col md={6}>
                    <FormInput
                      id="viewProductId"
                      label="Product Id"
                      value={selectedProduct?.productCode || ''}
                      onChange={() => { }}
                    />
                  </Col>
                  <Col md={6}>
                    <FormInput
                      id="viewVendor"
                      label="Vendor"
                      value="Vendor A"
                      onChange={() => { }}
                    />
                  </Col>
                  <Col md={6}>
                    <FormInput
                      id="viewProductCode"
                      label="Product Code"
                      value={selectedProduct?.productCode || ''}
                      onChange={() => { }}
                    />
                  </Col>
                  <Col md={6}>
                    <FormInput
                      id="viewVProductCode"
                      label="V. Product Code"
                      value={selectedProduct?.barcode || ''}
                      onChange={() => { }}
                    />
                  </Col>
                  <Col md={6}>
                    <SelectInput
                      formData={{ type: 'Product' }}
                      setFormData={() => { }}
                      label="Type"
                      id="type"
                      options={['Product', 'Service']}
                    />
                  </Col>
                  <Col md={6}>
                    <SelectInput
                      formData={{ marginType: 'Fixed' }}
                      setFormData={() => { }}
                      label="Margin Type"
                      id="marginType"
                      options={['Fixed', 'Percentage', 'Variable']}
                    />
                  </Col>
                  <Col md={6}>
                    <SelectInput
                      formData={{ subtype: 'New' }}
                      setFormData={() => { }}
                      label="Subtype"
                      id="subtype"
                      options={['New', 'Used']}
                    />
                  </Col>
                  <Col md={6}>
                    <div className="form-check mt-4">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id="viewActive"
                        checked={true}
                        readOnly
                      />
                      <Label className="form-check-label" htmlFor="viewActive">
                        Active
                      </Label>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="form-check mt-4">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id="viewOrderable"
                        checked={false}
                        readOnly
                      />
                      <Label className="form-check-label" htmlFor="viewOrderable">
                        Orderable
                      </Label>
                    </div>
                  </Col>
                  <Col md={6}>
                    <SelectInput
                      formData={{ category: 'General' }}
                      setFormData={() => { }}
                      label="Category"
                      id="category"
                      options={['General', 'Outdoor', 'Garden', 'Tools']}
                    />
                  </Col>
                  <Col md={6}>
                    <div className="form-check mt-4">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id="viewTaxable"
                        checked={false}
                        readOnly
                      />
                      <Label className="form-check-label" htmlFor="viewTaxable">
                        Taxable
                      </Label>
                    </div>
                  </Col>
                  <Col md={6}>
                    <SelectInput
                      formData={{ subcategory: 'Standard' }}
                      setFormData={() => { }}
                      label="Subcategory"
                      id="subcategory"
                      options={['Standard', 'Premium', 'Basic']}
                    />
                  </Col>
                  <Col md={6}>
                    <FormInput
                      id="viewBrand"
                      label="Brand"
                      value="Brand A"
                      onChange={() => { }}
                    />
                  </Col>
                  <Col md={12}>
                    <div className="mb-3">
                      <Label className="form-label mb-0">Description</Label>
                      <div className="d-flex">
                        <textarea
                          className="form-control form-control-sm"
                          rows={3}
                          value={selectedProduct?.productName || ''}
                          readOnly
                        />
                        <button className="btn btn-light btn-sm ms-2" type="button">
                          <i className="ri-search-line"></i>
                        </button>
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <FormInput
                      id="viewBrandProduct"
                      label="Brand Product"
                      value="Brand Product A"
                      onChange={() => { }}
                    />
                  </Col>
                  <Col md={6}>
                    <FormInput
                      id="viewBarcode"
                      label="Barcode"
                      value={selectedProduct?.barcode || ''}
                      onChange={() => { }}
                    />
                  </Col>
                  <Col md={12}>
                    <div className="mb-3">
                      <Label className="form-label mb-0">Extended Details</Label>
                      <textarea
                        className="form-control form-control-sm"
                        rows={2}
                        value="Next Delivery Date: Feb 5 2018 8:33AM"
                        readOnly
                      />
                    </div>
                  </Col>
                </Row>
              </Col>

              {/* Right Column */}
              <Col md={6}>
                <Row>
                  <Col md={12}>
                    <SelectInput
                      formData={{ location: 'Default Location' }}
                      setFormData={() => { }}
                      label="Location"
                      id="location"
                      options={['Default Location', 'Warehouse A', 'Warehouse B']}
                    />
                  </Col>
                  <Col md={6}>
                    <FormInput
                      id="viewAvailable"
                      label="Available"
                      value={selectedProduct?.available?.toString() || '0'}
                      onChange={() => { }}
                    />
                  </Col>
                  <Col md={6}>
                    <FormInput
                      id="viewInWH"
                      label="In WH"
                      value={selectedProduct?.inWH?.toString() || '0'}
                      onChange={() => { }}
                    />
                  </Col>
                  <Col md={6}>
                    <FormInput
                      id="viewAllocated"
                      label="Allocated"
                      value={selectedProduct?.allocated?.toString() || '0'}
                      onChange={() => { }}
                    />
                  </Col>
                  <Col md={6}>
                    <FormInput
                      id="viewPending"
                      label="Pending"
                      value="0"
                      onChange={() => { }}
                    />
                  </Col>
                  <Col md={6}>
                    <FormInput
                      id="viewBackOrder"
                      label="Back Order"
                      value="0"
                      onChange={() => { }}
                    />
                  </Col>
                  <Col md={6}>
                    <FormInput
                      id="viewOnOrder"
                      label="On Order"
                      value={'0'}
                      onChange={() => { }}
                    />
                  </Col>
                  <Col md={12} className="mt-4">
                    <FormInput
                      id="viewDutyPercent"
                      label="Duty %"
                      value="10"
                      onChange={() => { }}
                    />
                  </Col>
                  <Col md={12} className="mt-5">
                    <div className="d-flex gap-2 justify-content-end">

                    </div>
                  </Col>

                </Row>
              </Col>
            </Row>
          </TabPane>
          <TabPane tabId="4">
            <Form>
              <Row className="mb-2">
                <Col xs={12} md={6}>
                  <FormInput
                    id="newProductName"
                    label="Name"
                    value={newProductForm.name}
                    onChange={val => handleNewProductChange('name', val)}
                    required
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col md={3}>
                  <FormInput
                    id="newProductCode"
                    label="Product Code"
                    value={newProductForm.productCode}
                    onChange={val => handleNewProductChange('productCode', val)}
                  />
                </Col>
                <Col md={3}>
                  <FormInput
                    id="newVendor"
                    label="Vendor"
                    value={newProductForm.vendor}
                    onChange={val => handleNewProductChange('vendor', val)}
                    hasSearchButton
                  />
                </Col>
                <Col md={3}>
                  <FormInput
                    id="newVendorProductCode"
                    label="V. Product Code"
                    value={newProductForm.vendorProductCode}
                    onChange={val => handleNewProductChange('vendorProductCode', val)}
                  />
                </Col>
                <Col md={3}>
                  <SelectInput
                    formData={newProductForm}
                    setFormData={setNewProductForm}
                    label="Type"
                    id="type"
                    options={typeOptions}
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col md={3}>
                  <SelectInput
                    formData={newProductForm}
                    setFormData={setNewProductForm}
                    label="Subtype"
                    id="subtype"
                    options={subtypeOptions}
                  />
                </Col>
                <Col md={3}>
                  <SelectInput
                    formData={newProductForm}
                    setFormData={setNewProductForm}
                    label="Margin Type"
                    id="marginType"
                    options={marginTypeOptions}
                  />
                </Col>
                <Col md={3}>
                  <SelectInput
                    formData={newProductForm}
                    setFormData={setNewProductForm}
                    label="Category"
                    id="category"
                    options={categoryOptions}
                  />
                </Col>
                <Col md={3}>
                  <SelectInput
                    formData={newProductForm}
                    setFormData={setNewProductForm}
                    label="Subcategory"
                    id="subcategory"
                    options={subcategoryOptions}
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col md={6}>
                  <FormGroup>
                    <Label className="form-label mb-0">Description</Label>
                    <Input
                      type="textarea"
                      value={newProductForm.description}
                      onChange={e => handleNewProductChange('description', e.target.value)}
                      bsSize="sm"
                      rows={2}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row className="mb-2">
                <Col md={3}>
                  <FormInput
                    id="newBrand"
                    label="Brand"
                    value={newProductForm.brand}
                    onChange={val => handleNewProductChange('brand', val)}
                    hasSearchButton
                  />
                </Col>
                <Col md={3}>
                  <FormInput
                    id="newBrandProductCode"
                    label="Brand Product Code"
                    value={newProductForm.brandProductCode}
                    onChange={val => handleNewProductChange('brandProductCode', val)}
                  />
                </Col>
                <Col md={3}>
                  <FormInput
                    id="newBarcode"
                    label="Barcode"
                    value={newProductForm.barcode}
                    onChange={val => handleNewProductChange('barcode', val)}
                  />
                </Col>
                <Col md={3}>
                  <FormInput
                    id="newOrderQty"
                    label="Order Qty"
                    value={newProductForm.orderQty}
                    onChange={val => handleNewProductChange('orderQty', val)}
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col md={2}>
                  <FormInput
                    id="newPurchaseExc"
                    label="Purchase Exc"
                    value={newProductForm.purchaseExc}
                    onChange={val => handleNewProductChange('purchaseExc', val)}
                    hasSearchButton
                    iconClass="ri-calculator-line"
                  />
                </Col>
                <Col md={2}>
                  <FormGroup>
                    <Label className="form-label mb-0">Purchase Tax</Label>
                    <Input
                      type="select"
                      value={newProductForm.purchaseTax}
                      onChange={e => handleNewProductChange('purchaseTax', e.target.value)}
                      bsSize="sm"
                    >
                      {purchaseTaxOptions.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormInput
                    id="newPurchaseInc"
                    label="Purchase Inc"
                    value={newProductForm.purchaseInc}
                    onChange={val => handleNewProductChange('purchaseInc', val)}
                    hasSearchButton
                    iconClass="ri-calculator-line"
                  />
                </Col>
                <Col md={2}>
                  <FormInput
                    id="newMarkup"
                    label="Markup %"
                    value={newProductForm.markup}
                    onChange={val => handleNewProductChange('markup', val)}
                  />
                </Col>
                <Col md={2}>
                  <FormInput
                    id="newSellExc"
                    label="Sell Exc"
                    value={newProductForm.sellExc}
                    onChange={val => handleNewProductChange('sellExc', val)}
                    hasSearchButton
                    iconClass="ri-calculator-line"
                  />
                </Col>
                <Col md={2}>
                  <FormGroup>
                    <Label className="form-label mb-0">Sell Tax</Label>
                    <Input
                      type="select"
                      value={newProductForm.sellTax}
                      onChange={e => handleNewProductChange('sellTax', e.target.value)}
                      bsSize="sm"
                    >
                      {sellTaxOptions.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
              </Row>
              <Row className="mb-2">
                <Col md={2}>
                  <FormInput
                    id="newSellInc"
                    label="Sell Inc"
                    value={newProductForm.sellInc}
                    onChange={val => handleNewProductChange('sellInc', val)}
                    hasSearchButton
                    iconClass="ri-calculator-line"
                  />
                </Col>
                <Col md={2}>
                  <FormGroup>
                    <Label className="form-label mb-0">NZ Status</Label>
                    <Input
                      type="select"
                      value={newProductForm.nzStatus}
                      onChange={e => handleNewProductChange('nzStatus', e.target.value)}
                      bsSize="sm"
                    >
                      {nzStatusOptions.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormInput
                    id="newPickQuantity"
                    label="Pick Quantity"
                    value={newProductForm.pickQuantity}
                    onChange={val => handleNewProductChange('pickQuantity', val)}
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col md="auto">
                  <FormGroup check className="d-flex align-items-center gap-2 mb-0">
                    <Input
                      type="checkbox"
                      checked={newProductForm.active}
                      onChange={() => handleNewProductCheckbox('active')}
                      bsSize="sm"
                      id="newActive"
                    />
                    <Label htmlFor="newActive" className="form-label mb-0">Active</Label>
                  </FormGroup>
                </Col>
                <Col md="auto">
                  <FormGroup check className="d-flex align-items-center gap-2 mb-0">
                    <Input
                      type="checkbox"
                      checked={newProductForm.orderable}
                      onChange={() => handleNewProductCheckbox('orderable')}
                      bsSize="sm"
                      id="newOrderable"
                    />
                    <Label htmlFor="newOrderable" className="form-label mb-0">Orderable</Label>
                  </FormGroup>
                </Col>
                <Col md="auto">
                  <FormGroup check className="d-flex align-items-center gap-2 mb-0">
                    <Input
                      type="checkbox"
                      checked={newProductForm.taxable}
                      onChange={() => handleNewProductCheckbox('taxable')}
                      bsSize="sm"
                      id="newTaxable"
                    />
                    <Label htmlFor="newTaxable" className="form-label mb-0">Taxable</Label>
                  </FormGroup>
                </Col>
                <Col md="auto">
                  <FormGroup check className="d-flex align-items-center gap-2 mb-0">
                    <Input
                      type="checkbox"
                      checked={newProductForm.addToAll}
                      onChange={() => handleNewProductCheckbox('addToAll')}
                      bsSize="sm"
                      id="newAddToAll"
                    />
                    <Label htmlFor="newAddToAll" className="form-label mb-0">Add to All</Label>
                  </FormGroup>
                </Col>
                <Col md="auto">
                  <FormGroup check className="d-flex align-items-center gap-2 mb-0">
                    <Input
                      type="checkbox"
                      checked={newProductForm.priceList}
                      onChange={() => handleNewProductCheckbox('priceList')}
                      bsSize="sm"
                      id="newPriceList"
                    />
                    <Label htmlFor="newPriceList" className="form-label mb-0">Price List</Label>
                  </FormGroup>
                </Col>
                <Col md="auto">
                  <FormGroup check className="d-flex align-items-center gap-2 mb-0">
                    <Input
                      type="checkbox"
                      checked={newProductForm.location}
                      onChange={() => handleNewProductCheckbox('location')}
                      bsSize="sm"
                      id="newLocation"
                    />
                    <Label htmlFor="newLocation" className="form-label mb-0">Location</Label>
                  </FormGroup>
                </Col>
                <Col md="auto">
                  <FormGroup check className="d-flex align-items-center gap-2 mb-0">
                    <Input
                      type="checkbox"
                      checked={newProductForm.dangerousGoods}
                      onChange={() => handleNewProductCheckbox('dangerousGoods')}
                      bsSize="sm"
                      id="newDangerousGoods"
                    />
                    <Label htmlFor="newDangerousGoods" className="form-label mb-0">Dangerous Goods</Label>
                  </FormGroup>
                </Col>
              </Row>
            </Form>
          </TabPane>
        </TabContent>


      </ModalBody>
      <ModalFooter className="d-flex justify-content-between">
        {activeTab === '1' ? (
          <button
            className="btn btn-soft-primary btn-sm"
            style={{ minWidth: 80 }}
            onClick={handleShowDetails}
            disabled={!selectedProduct}
          >
            {showDetails ? 'Hide Details' : 'Show Details'}
          </button>
        ) : (
          <div></div>
        )}
        <div className="d-flex gap-2 justify-content-end">

          {activeTab === '4' ? (
            // New Product tab buttons
            <>
              <button className="btn btn-soft-warning btn-sm" style={{ minWidth: 80 }} onClick={handleNewProductClear}>Clear</button>
              <button className="btn btn-soft-primary btn-sm" style={{ minWidth: 80 }} onClick={() => {/* TODO: implement create */ }}>Create</button>
              <button className="btn btn-soft-success btn-sm" style={{ minWidth: 80 }} onClick={() => {/* TODO: implement create & pick */ }}>Create & Pick</button>
              <button className="btn btn-soft-info btn-sm" style={{ minWidth: 80 }} onClick={() => {/* TODO: implement generate barcode */ }}>Generate Barcode</button>
            </>
          ) : activeTab === '3' ? (
            // View Product tab buttons
            <>
              <button className="btn btn-soft-primary btn-sm" style={{ minWidth: 80 }}>Prev</button>
              <button className="btn btn-soft-primary btn-sm" style={{ minWidth: 80 }}>Next</button>
              <button className="btn btn-soft-success btn-sm" style={{ minWidth: 80 }}>Pick</button>
              <button className="btn btn-soft-info btn-sm" style={{ minWidth: 80 }}>Show Image</button>
            </>
          ) : activeTab === '1' ? (
            // Search tab buttons
            <>
              <button className="btn btn-soft-primary btn-sm me-2" style={{ minWidth: 80 }} onClick={handleShowDetails}>Configurator</button>
              <button className="btn btn-soft-warning btn-sm me-2" style={{ minWidth: 80 }} onClick={handleClears}>Clear</button>
              <button className="btn btn-soft-success btn-sm me-2" style={{ minWidth: 80 }} onClick={handleAdd} disabled={!selectedProduct}>Add & Continue</button>
              <button className="btn btn-soft-success btn-sm me-2" style={{ minWidth: 80 }} onClick={ selectionModal === 'qty' ? handlePickQty : handleAdd} disabled={!selectedProduct}>Pick</button>
            </>
          ) : null}
          <button className="btn btn-soft-dark btn-sm" style={{ minWidth: 80 }} onClick={handleCancel}>Cancel</button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ParentProductModal; 